import AbstractWidget, { lengthWithUnit } from './AbstractWidget';
import { isNullOrWhitespace } from '../utils/object';

export default class ExplorerWidget extends AbstractWidget {
    data = [];

    // Render here is slightly special, because it can _detect_ if the calling (React) class has laid out some of the HTML before calling this method.
    // dataProvider & onItemChange only used by Explorer Widget
    render = async (sources, onItemChange) => {
        if (
            this.container !== undefined &&
            this.container !== null &&
            this.design !== undefined &&
            this.design !== null
        ) {
            const doc = this.container.ownerDocument;
            const topWidget = this.container.closest('.rb-report-widget');
            const settings = {
                ...ExplorerWidget.getDefaults(),
                ...this.design
            };

            // Style settings.
            let style = 'box-sizing:border-box;overflow:auto;';

            if (!isNullOrWhitespace(settings.height)) style += `height: ${settings.height};`;

            // Border.
            if (!isNullOrWhitespace(settings.borderWidth))
                style += `border-width: ${lengthWithUnit(settings.borderWidth)};`;
            if (!isNullOrWhitespace(settings.borderRadius))
                style += `border-radius: ${lengthWithUnit(settings.borderRadius)};`;
            if (!isNullOrWhitespace(settings.borderStyle))
                style += `border-style: ${settings.borderStyle.toLowerCase()};`;
            if (!isNullOrWhitespace(settings.borderColor)) style += `border-color: ${settings.borderColor};`;
            if (!isNullOrWhitespace(settings.controlBackground)) style += `background: ${settings.controlBackground};`;

            // Scrollbox container.
            const scrollbox = doc.createElement('div');
            scrollbox.setAttribute(
                'class',
                `rb--generated ia-text-box ia-explorer-widget ${settings.cssClass}}`.trim()
            );
            scrollbox.setAttribute('style', style);

            // Explorer widget container.
            const explorer = doc.createElement('div');
            explorer.setAttribute('style', `padding:0.5rem`);
            scrollbox.appendChild(explorer);
            this.container.appendChild(scrollbox);

            // This handler gets called when an item in the explorer is clicked.
            const clickHandler = async (item) => {
                if (item.type === 'leaf')
                    this.data.forEach((itm) => {
                        if (itm.type === 'leaf') itm.selected = itm.id === item.id;
                    });
                else item.expanded = !item.expanded;
                onItemChange(item);
                this.renderExplorer({
                    doc,
                    container: explorer,
                    onClick: async (item) => {
                        await clickHandler(item);
                    }
                });
            };

            // Get full model and explorer data on first render only.
            this.data = [];
            const model = await this.getDataModel(sources);
            this.modelToExplorerData({ themes: model.rootThemes });

            // Render the explorer.
            this.renderExplorer({
                doc,
                container: explorer,
                onClick: async (item) => {
                    await clickHandler(item);
                }
            });

            // Remove placeholder.
            if (topWidget !== null)
                topWidget.setAttribute(
                    'class',
                    topWidget.getAttribute('class').replace(/\s(placeholder|errored)/g, '')
                );
        }
    };

    static getDefaults = () => {
        return {
            ...AbstractWidget.getDefaults()
        };
    };

    // Get the data model
    getDataModel(dataSources) {
        return new Promise((resolve, reject) => {
            for (let ds of dataSources) {
                if (typeof ds.connector !== 'undefined') {
                    // Not wanting the stored one, or stored one
                    // is not there, or it failed to parse...
                    ds.connector
                        .getDataModel(ds.config.coreLayer)
                        .then((catalogModel) => {
                            catalogModel.normalize(); // Strip empty leaves
                            resolve(catalogModel);
                        })
                        .catch((dataModelErr) => {
                            reject(dataModelErr);
                        });
                }
            }
        });
    }

    // Convert data model into an array that can be used as "data" in "renderExplorer"
    modelToExplorerData({ themes = [] }) {
        themes.forEach((thm) => {
            this.data.push({
                id: thm._id,
                label: thm._shortName ?? thm._name,
                title: thm._name ?? thm._shortName,
                type: 'branch',
                parent: thm._theme,
                value: thm
            });
            thm._indicators.forEach((ind) => {
                let label = ind._shortName ?? ind.Name ?? ind.name;
                let title = ind.Name ?? ind.name ?? ind._shortName;
                if (ind.data_Type != null) {
                    label += ` (${ind.data_Type})`;
                    title += ` (${ind.data_Type})`;
                }
                this.data.push({
                    id: ind._id,
                    label,
                    title,
                    type: 'leaf',
                    parent: thm._id,
                    value: ind
                });
            });
            this.modelToExplorerData({ themes: thm._themes });
        });
    }

    // Render the actual widget to the dom
    renderExplorer({ doc, container, startIndent = 0, indentSize = 20, onClick = () => {} }) {
        container.innerHTML = '';

        const folderIcon = `<svg class="iconColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M15 3l-6.7.002L6.98 2H4L2.7 3H1v11h14zm-1 10H2V7h12zm0-7H2V4h1.04l1.3-1h2.304l1.319 1H14z"/><path fill="none" d="M0 0h16v16H0z"/></svg>`;
        const folderIconOpen = `<svg class="iconColor" xxmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M8.3 3.002L6.98 2H4L2.7 3H1v11h13l2-8h-2V3zM2 4h1.041L4.34 3h2.304l1.319 1.002L13 4v2H9L7.7 7h-4L2 11.408zm12.719 3l-1.5 6H2.458l1.929-5H8.04l1.3-1z"/><path fill="none" d="M0 0h16v16H0z"/></svg>`;
        const fileIcon = `<svg class="iconColor" xxmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M8 0a4.96 4.96 0 0 0-4.9 5.086C3.1 7.893 5.778 11.692 8 16c2.222-4.308 4.9-8.107 4.9-10.914A4.96 4.96 0 0 0 8 0zm0 7.166A2.166 2.166 0 1 1 10.13 5 2.147 2.147 0 0 1 8 7.166z"/><path fill="none" d="M0 0h16v16H0z"/></svg>`;
        const spinnerIcon = `<svg class="iconColor" xid="spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M9 1.282V.272a7.8 7.8 0 1 1-2 0v1.01a6.8 6.8 0 1 0 2 0z"/><path fill="none" d="M0 0h16v16H0z"/></svg>`;

        const addItems = (arrItems, indent = startIndent) => {
            if (arrItems.length > 0) {
                for (let item of arrItems) {
                    const folder = item.expanded === true ? folderIconOpen : folderIcon;
                    const folderOrFile = item.type === 'branch' ? folder : fileIcon;
                    const icon = item.loading === true ? spinnerIcon : folderOrFile;
                    const isDisabled = item.selected === true || item.loading === true;
                    const itemStyle = item.selected === true ? `item itemSelected` : `item`;

                    // Explorer item
                    const btn = doc.createElement('button');
                    btn.setAttribute('class', itemStyle);
                    btn.setAttribute('title', item.title);
                    btn.setAttribute('aria-label', item.title);
                    btn.setAttribute('style', `cursor: ${isDisabled === true ? 'default' : 'pointer'}`);
                    btn.setAttribute('class', itemStyle);
                    btn.innerHTML = `<span class="icon" style="margin-left: ${indent}px" aria-hidden="true" role="img">${icon}</span><span class="label">${item.label}</span>`;
                    btn.addEventListener('click', (e) => {
                        if (!isDisabled) onClick(item);
                    });
                    container.appendChild(btn);

                    // Only render children if branch is expanded
                    if (item.expanded === true) {
                        const children = this.data.filter((itm) => itm.parent === item.id);
                        addItems(children, indent + indentSize);
                    }
                }
            }
        };
        addItems(
            this.data.filter((item) => item.parent === null),
            startIndent
        );
    }
}
